import { useProduct } from '@core/hooks/useCatalog';
import { getCartLineSelections } from '@core/utils/cartSelections';
import { getLineUomLabel } from '@core/utils/lineUnits';
import { getLineDisplayTotal } from '@core/utils/lineTotals';
import Price from '@components/base/Price';
import UpsellBadge from '@components/base/UpsellBadge';

function groupSelections(selections, kind) {
  const groups = new Map();

  for (const selection of selections) {
    const groupId = selection.groupId ?? `${kind}-group`;
    const groupName = selection.groupName ?? (kind === 'variant' ? 'Variant' : 'Modifier');
    const key = `${groupId}::${groupName}`;
    if (!groups.has(key)) {
      groups.set(key, { groupName, items: [] });
    }
    groups.get(key).items.push(selection);
  }

  return Array.from(groups.values());
}

function splitServiceItemsByOrigin(serviceItems) {
  const variantItems = [];
  const alternativeUnitItems = [];

  for (const item of serviceItems) {
    if (item.alternativeUnit) {
      alternativeUnitItems.push(item);
    } else {
      variantItems.push(item);
    }
  }

  return { variantItems, alternativeUnitItems };
}

function AddOnChips({ items }) {
  if (items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 14 }}>
      {items.map((item) => (
        <span
          key={`${item.variantGroupId ?? item.alternativeUnit ?? 'service'}-${item.variantId ?? ''}-${item.serviceItemId}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '0.125rem 0.45rem',
            borderRadius: 999,
            background: 'rgba(15, 118, 110, 0.08)',
            color: '#0f766e',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <span>+ {item.name}</span>
          {item.priceDelta ? <Price amount={item.priceDelta} /> : null}
        </span>
      ))}
    </div>
  );
}

export default function CartLineDetails({ line, t, showTitle = true, showPricing = true }) {
  const productQuery = useProduct(line.itemId);
  const selections = getCartLineSelections({
    ...line,
    item: productQuery.data ?? line.item,
  });
  const groupedVariants = groupSelections(selections.variants, 'variant');
  const groupedModifiers = groupSelections(selections.modifiers, 'modifier');
  const { variantItems: variantServiceItems, alternativeUnitItems: alternativeUnitServiceItems } =
    splitServiceItemsByOrigin(selections.serviceItems ?? []);
  const uomLabel = getLineUomLabel({
    ...line,
    item: productQuery.data ?? line.item,
  });

  const selectedVariantIds = new Set(
    selections.variants.map((variant) => variant.variantId).filter(Boolean),
  );
  const unmatchedVariantServiceItems = [];
  const variantServiceItemsByVariantId = new Map();
  for (const item of variantServiceItems) {
    if (item.variantId && selectedVariantIds.has(item.variantId)) {
      if (!variantServiceItemsByVariantId.has(item.variantId)) {
        variantServiceItemsByVariantId.set(item.variantId, []);
      }
      variantServiceItemsByVariantId.get(item.variantId).push(item);
    } else {
      unmatchedVariantServiceItems.push(item);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {showTitle ? (
        <strong>
          {line.itemName}
          <UpsellBadge line={line} t={t} />
        </strong>
      ) : null}
      {uomLabel || alternativeUnitServiceItems.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {uomLabel ? <span>{uomLabel}</span> : null}
          <AddOnChips items={alternativeUnitServiceItems} />
        </div>
      ) : null}
      {groupedVariants.map((group) => {
        const addOns = group.items.flatMap(
          (item) => variantServiceItemsByVariantId.get(item.variantId) ?? [],
        );
        return (
          <div
            key={`${group.groupName}-${group.items.map((item) => item.variantId).join(',')}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                padding: '0.125rem 0.45rem',
                borderRadius: 999,
                background: 'rgba(17, 24, 39, 0.06)',
                color: '#344054',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {group.groupName ? `${group.groupName}: ` : ''}
              {group.items.map((item) => item.variantName).join(', ')}
            </span>
            <AddOnChips items={addOns} />
          </div>
        );
      })}
      <AddOnChips items={unmatchedVariantServiceItems} />
      {groupedModifiers.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {groupedModifiers.map((group) => (
            <span
              key={`${group.groupName}-${group.items.map((item) => item.modifierId).join(',')}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.125rem 1rem',
                borderRadius: 999,
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#b42318',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {group.groupName ? `${group.groupName}: ` : ''}
              {group.items.map((item) => `${item.modifierName} *${item.quantity ?? 1}`).join(', ')}
            </span>
          ))}
        </div>
      ) : null}
      {showPricing ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            fontSize: 12,
            color: 'var(--color-text-secondary, #475467)',
          }}
        >
          <span>
            {t('each', 'Each')}:{' '}
            <Price amount={line.basePrice ?? line.unitPrice ?? line.price ?? 0} />
          </span>
          <span>
            {t('total', 'Total')}:{' '}
            <Price
              amount={
                getLineDisplayTotal(line) ||
                (line.basePrice ?? line.unitPrice ?? line.price ?? 0) * (line.quantity ?? 1)
              }
            />
          </span>
        </div>
      ) : null}
    </div>
  );
}
